import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Clock, ArrowRight } from "lucide-react";
import { usePageMeta } from "../../hooks/usePageMeta";
import decorating1 from "../../assets/images/decorating1.webp";
import ceilings1 from "../../assets/images/ceilings1.webp";
import flooring1 from "../../assets/images/flooring1.webp";

const OfficeRefurbishment = () => {
  usePageMeta(
    "Office Refurbishment | Sandhurst Interiors",
    "Office refurbishment specialists in the South East. From refresh projects to full refurb—partitioning, ceilings, decorating, flooring and project management. 30+ years experience."
  );

  return (
    <div className="min-h-screen">
      <section className="section-padding bg-primary-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Office Refurbishment in the South East
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From a single office refresh to a full building refurbishment—we
              deliver high-quality commercial refurbishment projects with
              minimal disruption and lasting results.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700"
          >
            <p>
              For over 30 years, Sandhurst Interiors has delivered office and
              commercial refurbishment for clients across the South East. As a
              family-run business, we combine traditional craftsmanship with
              modern solutions so every space is built to last and tailored to
              how you work.
            </p>
            <p>
              Our refurbishment work includes new glazed partitions, suspended
              ceilings, LED lighting, modern flooring and redecoration—improving
              acoustic privacy, energy efficiency and the look and feel of your
              workspace. We work on projects of all sizes, from a single office
              to complete buildings, in the private and public sector including
              schools and hospitals.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            What We Offer on Refurbishment Projects
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Partitioning (soundproof, glazed, modular)",
              "Suspended and MF plasterboard ceilings",
              "Dry-lining, taping & jointing, plastering",
              "Interior and exterior decorating",
              "Flooring (carpet tiles, vinyl, specialist finishes)",
              "Electrical and data upgrades",
              "Air conditioning and ventilation",
              "Space planning and layout changes",
              "Full project and site management",
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg"
              >
                <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={decorating1}
                  alt="Office decorating and refurbishment"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={ceilings1}
                  alt="Suspended ceilings in office refurbishment"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="col-span-2 aspect-video rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={flooring1}
                  alt="Office flooring installation"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center space-x-3 text-gray-700">
                <Clock className="w-6 h-6 text-primary-600 flex-shrink-0" />
                <span className="font-medium">On time, on budget</span>
              </div>
              <p className="text-gray-700">
                Our professional team works closely with you so your
                refurbishment meets your expectations and needs. We provide
                dedicated site supervision and clear communication, and we
                scale from facilities maintenance to full turnkey office
                transformations—so you get the right level of support for your
                project.
              </p>
              <Link
                to="/contact#contact-form"
                onClick={() => sessionStorage.setItem("scrollToContactForm", "true")}
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>Discuss Your Refurbishment</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to refurbish your office?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Speak with our team today about transforming your office, warehouse
              or facility.
            </p>
            <Link
              to="/contact#contact-form"
              onClick={() => sessionStorage.setItem("scrollToContactForm", "true")}
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 inline-flex items-center space-x-2"
            >
              <Building2 className="w-4 h-4" />
              <span>Get in Touch</span>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OfficeRefurbishment;
